import { useMemo } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { INVOICE_STATUS_COLORS } from "@/lib/dashboardColors";
import { formatCurrency } from "@/utils/format";

const STATUS_LABELS = {
  paid: "Bezahlt",
  sent: "Gesendet",
  created: "Entwurf",
  overdue: "Überfällig",
  cancelled: "Storniert",
  unknown: "Unbekannt",
};

const STATUS_COLORS = INVOICE_STATUS_COLORS;

const StatusLegend = ({ statuses }) => (
  <div className="flex flex-wrap gap-4 text-xs text-slate-500" data-testid="invoice-status-legend">
    {statuses.map((status) => (
      <div key={status} className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
        {STATUS_LABELS[status] || status}
      </div>
    ))}
  </div>
);

const InvoiceRevenueTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const statusBreakdown = payload?.[0]?.payload?.statusBreakdown || {};

  return (
    <div className="rounded-lg border bg-white p-3 text-xs text-gray-700 shadow-sm">
      <div className="font-semibold text-gray-900">{label}</div>
      {Object.entries(statusBreakdown).map(([status, values]) => (
        <div key={status} className="mt-1">
          <div className="font-medium text-gray-800">{STATUS_LABELS[status] || status}</div>
          <div>Netto: {formatCurrency(values.net || 0)}</div>
          <div>USt: {formatCurrency(values.vat || 0)}</div>
        </div>
      ))}
    </div>
  );
};

export default function InvoiceRevenueChart({ data = [], isLoading, error }) {
  const gridColor = useMemo(() => {
    if (typeof document === "undefined") return "#e2e8f0";
    return document.documentElement.classList.contains("dark") ? "#334155" : "#e2e8f0";
  }, []);
  const axisColor = "#475569";

  return (
    <Card data-testid="invoice-revenue-chart">
      <CardHeader>
        <CardTitle>Rechnungsumsatz</CardTitle>
      </CardHeader>
      <CardContent className="h-72">
        {isLoading && <Skeleton className="h-full w-full" data-testid="invoice-revenue-loading" />}
        {!isLoading && error && (
          <div className="text-sm text-red-600" data-testid="invoice-revenue-error">
            Umsatz-Daten konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !error && data.length === 0 && (
          <div className="text-sm text-gray-500" data-testid="invoice-revenue-empty">
            Keine Rechnungsdaten im Zeitraum.
          </div>
        )}
        {!isLoading && !error && data.length > 0 && (
          <div className="flex h-full flex-col">
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={200}>
                <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke={axisColor} tick={{ fill: axisColor }} />
                  <YAxis
                    stroke={axisColor}
                    tick={{ fill: axisColor }}
                    width={90}
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <Tooltip content={<InvoiceRevenueTooltip />} />
                  {Object.keys(STATUS_LABELS)
                    .filter((status) => data.some((row) => row[status] > 0))
                    .map((status) => (
                      <Bar
                        key={status}
                        dataKey={status}
                        stackId="status"
                        fill={STATUS_COLORS[status]}
                        name={STATUS_LABELS[status]}
                        radius={[0, 0, 0, 0]}
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="pt-2">
              <StatusLegend
                statuses={Object.keys(STATUS_LABELS).filter((status) =>
                  data.some((row) => row[status] > 0)
                )}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}